import React, { useMemo, useState } from "react";

import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import { Autocomplete, Button, TextField } from "@mui/material";

import { ConfirmationModal } from "../../../components/ConfirmationModal";
import { ModalSize, MuiModal } from "../../../components/mui/MuiModal";
import { CafsTreeNodeType } from "../../../types/cafs-tree-node-type";
import { useCafsMove } from "../api/useCafsMove";

// Helper function to flatten hierarchical options for autocomplete
const flattenHierarchicalOptions = (
  nodes: CafsTreeNodeType[],
  level: number = 0,
  parentPath: string = "",
): Array<{
  id: number | string;
  label: string;
  name: string;
  uniqueKey: string;
}> => {
  const options: Array<{
    id: number | string;
    label: string;
    name: string;
    uniqueKey: string;
  }> = [];

  nodes.forEach((node, index) => {
    const indent = "\u00A0".repeat(level * 4);
    const nodeName = `[${node.id}] ${node.name}` || "Unnamed";
    const label = `${indent}${level === 0 ? "" : "\u221F "}${nodeName}`;
    const uniqueKey = `${parentPath}-${index}-${node.id}`;

    options.push({
      id: node.id,
      label: label,
      name: nodeName,
      uniqueKey: uniqueKey,
    });

    if (node.childNodes && node.childNodes.length > 0) {
      options.push(
        ...flattenHierarchicalOptions(node.childNodes, level + 1, uniqueKey),
      );
    }
  });

  return options;
};

export type MoveCafsModalProps = {
  open: boolean;
  onClose: () => void;
  cafs: CafsTreeNodeType | undefined;
  allCafsNodes: CafsTreeNodeType[];
};

export function MoveCafsModal({
  open,
  onClose,
  cafs,
  allCafsNodes,
}: MoveCafsModalProps) {
  const [targetParentNode, setTargetParentNode] = useState<
    CafsTreeNodeType | undefined
  >();
  const [showConfirmation, setShowConfirmation] = useState(false);

  const { moveCafs, moveCafsPending } = useCafsMove({
    cafs: cafs,
    targetParentName: targetParentNode?.name ?? "",
    onSuccess: handleSuccess,
  });

  // Filter nodes by type to only show same type as selected CAFS
  const filteredNodes = useMemo(() => {
    if (!cafs?.type) return [];
    return allCafsNodes.filter(node => node.type === cafs.type);
  }, [allCafsNodes, cafs?.type]);

  // Flatten hierarchical options for autocomplete
  const cafsOptions = useMemo(
    () => flattenHierarchicalOptions(filteredNodes),
    [filteredNodes],
  );

  function handleSuccess() {
    handleCloseAll();
  }

  const handleCloseAll = () => {
    setTargetParentNode(undefined);
    setShowConfirmation(false);
    onClose();
  };

  const handleSelectParent = () => {
    if (!targetParentNode) return;
    setShowConfirmation(true);
  };

  const handleMove = () => {
    if (!targetParentNode) return;

    moveCafs({
      cafsId: cafs?.id!,
      type: cafs?.type,
      targetId: +targetParentNode.id,
    });
  };

  const handleCancelConfirmation = () => {
    setShowConfirmation(false);
  };

  if (showConfirmation && targetParentNode) {
    return (
      <ConfirmationModal
        onConfirm={handleMove}
        onCancel={handleCancelConfirmation}
        header="Confirm CAFS Move"
        variant="warning"
        okText="Move"
        icon={<AccountTreeOutlinedIcon />}
        disabled={moveCafsPending}
        text={
          <span className="text-shark">
            Are you sure you want to move this CAFS and all its children from
            parent{" "}
            <span className="font-semibold">{cafs?.parent || "root"}</span> to{" "}
            <span className="font-semibold">
              {targetParentNode.name} ({String(targetParentNode.id)})
            </span>
            ?
          </span>
        }
        open={showConfirmation}
      />
    );
  }

  const findNodeById = (
    nodes: CafsTreeNodeType[],
    id: string | number,
  ): CafsTreeNodeType | undefined => {
    for (const node of nodes) {
      if (String(node.id) === String(id)) {
        return node;
      }
      if (node.childNodes) {
        const found = findNodeById(node.childNodes, id);
        if (found) return found;
      }
    }
    return undefined;
  };

  return (
    <MuiModal
      open={open}
      size={ModalSize.Big}
      header="Move CAFS"
      onClose={handleCloseAll}>
      <div
        className="flex flex-col gap-4 w-full"
        onClick={e => e.stopPropagation()}
        onMouseDown={e => e.stopPropagation()}>
        <p className="text-shark">Select new parent CAFS:</p>
        <Autocomplete
          size="small"
          options={cafsOptions}
          value={cafsOptions.find(o => o.id === targetParentNode?.id) ?? null}
          onChange={(_, value) => {
            if (value) {
              const node = findNodeById(filteredNodes, value.id);
              setTargetParentNode(node);
            } else {
              setTargetParentNode(undefined);
            }
          }}
          getOptionLabel={option => option.label}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderInput={params => (
            <TextField
              {...params}
              label="Target Parent CAFS"
              placeholder="Select parent CAFS"
            />
          )}
          renderOption={(props, option) => (
            <li {...props} key={option.uniqueKey}>
              <span style={{ fontFamily: "monospace" }}>{option.label}</span>
            </li>
          )}
        />
        <div className="flex justify-end items-end gap-4">
          <Button
            color="primary"
            variant="contained"
            disabled={!targetParentNode}
            onClick={handleSelectParent}>
            Select
          </Button>
          <Button color="primary" variant="outlined" onClick={handleCloseAll}>
            Cancel
          </Button>
        </div>
      </div>
    </MuiModal>
  );
}
